import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useGmailComposeUrl } from "@/lib/mail";
import {
    ArrowLeft,
    Loader2,
    User,
    Activity,
    CalendarDays,
    Dumbbell,
    Mail,
    StickyNote,
    ClipboardList,
    Phone,
    MapPin,
    Heart,
    Save,
    CreditCard,
    ExternalLink,
    AlertTriangle,
    Undo2,
    RefreshCw,
    Play,
    Settings,
    Trash2,
    MessageSquare,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, TRACK_LABELS, calcAge } from "@/lib/permissions";
import { GRADE_LABELS, SPORT_LABELS } from "@/lib/athleteTaxonomy";
import AssessmentFormModal from "@/components/coach/AssessmentFormModal";
import BodyweightCard from "@/components/biometrics/BodyweightCard";
import AthleteWaiverStatusCard from "@/components/coach/AthleteWaiverStatusCard";
import EnrollmentLinkModal from "@/components/coach/EnrollmentLinkModal";
import SectionErrorBoundary from "@/components/coach/SectionErrorBoundary";
import SessionAccountingCard from "@/components/coach/SessionAccountingCard";
import RunWorkoutPickerModal from "@/components/coach/RunWorkoutPickerModal";
import AthleteSchedulePanel from "@/components/coach/AthleteSchedulePanel";
import { ActivePacksPanel } from "@/components/coach/PackPricingMatrix";
import SubscriptionStatusChip from "@/components/coach/SubscriptionStatusChip";
import DeadPackResolutionPanel from "@/components/coach/DeadPackResolutionPanel";
import WorkoutsAccessToggle from "@/components/coach/leadDetail/WorkoutsAccessToggle";
import OwnerUserAdminModal from "@/components/coach/OwnerUserAdminModal";
import AthleteEditModal from "@/components/coach/AthleteEditModal";
import SetupCardForClientButton from "@/components/coach/SetupCardForClientButton";
import PaymentHistoryPanel from "@/components/coach/leadDetail/PaymentHistoryPanel";
import AthleteSubscribeWidget from "@/components/coach/leadDetail/AthleteSubscribeWidget";
import ChargeAndSubscribePanel from "@/components/coach/leadDetail/ChargeAndSubscribePanel";
import ActiveSubscriptionsPanel from "@/components/coach/leadDetail/ActiveSubscriptionsPanel";
import { formatTimeDisplay } from "@/lib/schedule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { key: "info", label: "Info", icon: User },
    { key: "billing", label: "Billing", icon: CreditCard },
    { key: "schedule", label: "Schedule", icon: CalendarDays },
    { key: "attendance", label: "Attendance", icon: Activity },
];

// Legacy ?tab= deep-link redirects so existing links/shortcuts (like the
// "View attendance" button on Lead Detail) keep landing on the right place
// after the tab consolidation.
const LEGACY_TAB_MAP = {
    overview: "info",
    assessment: "info",
    workouts: "info",
    strikes: "info",
    comms: "info",
    notes: "info",
    sessions: "schedule",
};

export default function AthleteDossier() {
    const { athleteId } = useParams();
    const [searchParams] = useSearchParams();
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    // Honor ?tab=... so deep links from other parts of the app land on the
    // right tab. Old tab keys (attendance, workouts, sessions, etc.) are
    // remapped to their new home via LEGACY_TAB_MAP. Falls back to "info".
    const initialTab = (() => {
        const q = searchParams.get("tab") || "";
        if (TABS.some((t) => t.key === q)) return q;
        return LEGACY_TAB_MAP[q] || "info";
    })();
    const [tab, setTab] = useState(initialTab);
    const [assessOpen, setAssessOpen] = useState(false);
    const [ownerAdminOpen, setOwnerAdminOpen] = useState(false);
    const [athleteEditOpen, setAthleteEditOpen] = useState(false);
    const [coachNotes, setCoachNotes] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (!(user.role === "owner" || user.role === "coach")) {
            navigate("/dashboard", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/athletes/${athleteId}`, { withCredentials: true });
            setData(r.data);
            setCoachNotes(r.data?.athlete?.coach_notes || "");
        } catch (e) {
            toast.error("Couldn't load athlete", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (user?.role === "owner" || user?.role === "coach") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [athleteId, user]);

    const saveNotes = async () => {
        setSavingNotes(true);
        try {
            await axios.patch(
                `${API}/coach/athletes/${athleteId}/notes`,
                { coach_notes: coachNotes },
                { withCredentials: true },
            );
            toast.success("Notes saved");
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSavingNotes(false);
        }
    };

    if (loading || busy || !data) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>;
    }

    const { athlete, parent, assessment, workout_sessions, maxes, announcements } = data;
    const statusMeta = STATUS_META[parent?.status] || STATUS_META.lead;
    const age = calcAge(athlete.dob);

    return (
        <div data-testid="athlete-dossier" className="min-h-screen bg-black text-white">
            <div className="max-w-7xl mx-auto px-4 pt-28 pb-10">
                <div className="flex flex-col items-start gap-1.5 mb-6">
                    <Link to="/coach" className="inline-flex items-center gap-1 text-zinc-200 hover:text-white">
                        <ArrowLeft className="w-4 h-4" /> Coach console
                    </Link>
                    {parent?.user_id && (
                        <Link
                            to={`/coach/leads/${parent.user_id}`}
                            data-testid="dossier-back-to-family"
                            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-brand-red transition-colors"
                            title="Open the family / main account profile"
                        >
                            <ArrowLeft className="w-3 h-3" /> Back to {athlete?.last_name ? `${athlete.last_name} family` : "main profile"}
                        </Link>
                    )}
                </div>

                {/* Header */}
                <div className="flex flex-col md:flex-row flex-wrap md:items-start md:justify-between gap-4 md:gap-6 mb-8">
                    <div className="min-w-0">
                        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-wider break-words">
                            {athlete.first_name?.toUpperCase()} {athlete.last_name?.toUpperCase()}
                        </h1>
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                            <span className={`px-2 py-1 border ${statusMeta.className}`}>{statusMeta.label}</span>
                            {age !== null && <span className="px-2 py-1 border border-zinc-800 text-zinc-200">Age {age}</span>}
                            {athlete.track && <span className="px-2 py-1 border border-zinc-800 text-zinc-200">{TRACK_LABELS[athlete.track] || athlete.track}</span>}
                            {assessment && <span className="px-2 py-1 border border-brand-navy-700 text-brand-navy-200">Assessed</span>}
                            {parent?.user_id && (
                                <SubscriptionStatusChip userId={parent.user_id} athleteId={athlete.athlete_id} />
                            )}
                            {parent?.user_id && (
                                <TierCheckboxes
                                    userId={parent.user_id}
                                    tier={parent.tier || "non_member"}
                                    manual={!!parent.tier_manual}
                                    onSaved={(t, manual) => { data.parent = { ...parent, tier: t, tier_manual: manual }; }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                        {parent?.user_id && (
                            <button
                                type="button"
                                data-testid="dossier-message-parent"
                                onClick={async () => {
                                    // Sachin 2026-03-05: one-tap "Message" opens
                                    // (or reuses) a thread with the parent and
                                    // navigates to /coach/messages?t=<thread_id>.
                                    // The inbox composer picks up from there and
                                    // saves every send in the message thread.
                                    try {
                                        const r = await axios.post(
                                            `${process.env.REACT_APP_BACKEND_URL}/api/messages/start-thread`,
                                            { recipient_user_id: parent.user_id, body: "" },
                                            { withCredentials: true },
                                        );
                                        const tid = r.data?.thread_id;
                                        if (!tid) throw new Error("No thread returned");
                                        navigate(`/coach/messages?t=${encodeURIComponent(tid)}`);
                                    } catch (e) {
                                        toast.error("Couldn't open the thread", {
                                            description: e?.response?.data?.detail || e?.message,
                                        });
                                    }
                                }}
                                className="btn-ghost-tactical justify-center whitespace-nowrap"
                                title="Send a direct message to this athlete's parent — saved in Messages"
                            >
                                <MessageSquare className="w-4 h-4" /> Message
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="dossier-edit-athlete"
                            onClick={() => setAthleteEditOpen(true)}
                            className="btn-ghost-tactical justify-center whitespace-nowrap"
                            title="Edit this athlete's profile (name, email, sport, notes, etc.)"
                        >
                            <User className="w-4 h-4" /> Edit athlete
                        </button>
                        {user?.role === "owner" && parent?.user_id && (
                            <button
                                type="button"
                                data-testid="dossier-owner-admin"
                                onClick={() => setOwnerAdminOpen(true)}
                                className="btn-ghost-tactical justify-center whitespace-nowrap"
                                title="Owner: edit profile · promote/demote · delete"
                            >
                                <Settings className="w-4 h-4" /> Manage account
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="dossier-run-assessment"
                            onClick={() => setAssessOpen(true)}
                            className="btn-tactical justify-center whitespace-nowrap"
                        >
                            <ClipboardList className="w-4 h-4" /> {assessment ? "Edit assessment" : "Run assessment"}
                        </button>
                    </div>
                </div>

                {/* Tabs — on mobile we use a horizontally scrolling pill strip
                    so the user can SEE every tab (instead of an obscure dropdown
                    that just reads "OVERVIEW"). The active tab is clearly
                    highlighted with the brand-red accent. Most gym work happens
                    on phones — Schedule must be one tap away. */}
                <div className="mb-8">
                    {/* Mobile: scrolling pill tabs */}
                    <div className="md:hidden -mx-4 px-4 border-b border-zinc-800">
                        <div
                            data-testid="dossier-tabs-mobile"
                            className="flex gap-1.5 overflow-x-auto pb-px no-scrollbar snap-x"
                        >
                            {TABS.map((t) => {
                                const Icon = t.icon;
                                const on = tab === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        data-testid={`dossier-tab-${t.key}`}
                                        onClick={() => setTab(t.key)}
                                        className={`snap-start inline-flex items-center gap-1.5 px-3 py-2.5 text-[11px] tracking-[0.2em] uppercase border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                                            on
                                                ? "border-brand-red text-white"
                                                : "border-transparent text-zinc-300 hover:text-white"
                                        }`}
                                    >
                                        <Icon className="w-3.5 h-3.5" />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {/* Desktop: tab strip */}
                    <div className="hidden md:block border-b border-zinc-800 overflow-x-auto">
                        <div className="flex gap-1">
                            {TABS.map((t) => {
                                const Icon = t.icon;
                                const on = tab === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        data-testid={`dossier-tab-desktop-${t.key}`}
                                        onClick={() => setTab(t.key)}
                                        className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] tracking-[0.2em] uppercase border-b-2 transition-colors whitespace-nowrap ${
                                            on
                                                ? "border-brand-red text-white"
                                                : "border-transparent text-zinc-300 hover:text-white"
                                        }`}
                                    >
                                        <Icon className="w-3.5 h-3.5" />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Panels */}
                {tab === "info" && (
                    <>
                        <div className="mb-6 space-y-3">
                            <SectionErrorBoundary section="Bodyweight">
                                <BodyweightCard
                                    athleteId={athleteId}
                                    athleteName={athlete?.first_name}
                                />
                            </SectionErrorBoundary>
                            <SectionErrorBoundary section="Athlete waiver status">
                                <AthleteWaiverStatusCard athleteId={athleteId} />
                            </SectionErrorBoundary>
                        </div>
                        <SectionErrorBoundary section="Overview">
                            <OverviewPanel athlete={athlete} parent={parent} onAthleteRefresh={load} />
                        </SectionErrorBoundary>
                        <SectionErrorBoundary section="Assessment">
                            <AssessmentPanel assessment={assessment} onEdit={() => setAssessOpen(true)} />
                        </SectionErrorBoundary>
                        <SectionErrorBoundary section="Workouts">
                            <WorkoutsPanel
                                sessions={workout_sessions || []}
                                maxes={maxes || []}
                                athleteId={athleteId}
                                userId={parent?.user_id}
                                athleteFirstName={athlete?.first_name}
                                onMaxUpdated={load}
                            />
                        </SectionErrorBoundary>
                        {parent?.user_id && (
                            <SectionErrorBoundary section="Strikes">
                                <StrikesPanel
                                    userId={parent.user_id}
                                    parentName={parent?.name || athlete?.first_name || "Athlete"}
                                />
                            </SectionErrorBoundary>
                        )}
                        <SectionErrorBoundary section="Comms">
                            <CommsPanel announcements={announcements || []} />
                        </SectionErrorBoundary>
                        <SectionErrorBoundary section="Coach notes">
                            <NotesPanel
                                value={coachNotes}
                                onChange={setCoachNotes}
                                onSave={saveNotes}
                                saving={savingNotes}
                                updatedAt={athlete.coach_notes_updated_at}
                            />
                        </SectionErrorBoundary>
                    </>
                )}
                {tab === "schedule" && (
                    <SectionErrorBoundary section="Schedule">
                        <AthleteSchedulePanel athleteId={athleteId} athleteFirstName={athlete?.first_name} />
                    </SectionErrorBoundary>
                )}
                {tab === "attendance" && (
                    <SectionErrorBoundary section="Attendance">
                        <AttendancePanel athleteId={athleteId} athlete={athlete} />
                    </SectionErrorBoundary>
                )}
                {tab === "billing" && (
                    <>
                        {/* Sachin 2026-06: owner-only "Show workouts" toggle,
                            now PER ATHLETE — the flag lives on this athlete's
                            profile so siblings aren't affected. Falls back to
                            the legacy family flag if this athlete was never
                            explicitly set. */}
                        <SectionErrorBoundary section="Workouts access">
                            <WorkoutsAccessToggle
                                athleteId={athleteId}
                                athleteName={athlete?.first_name}
                                initialEnabled={
                                    athlete?.workouts_enabled !== undefined
                                        ? athlete.workouts_enabled
                                        : parent?.workouts_enabled
                                }
                            />
                        </SectionErrorBoundary>
                        {parent?.user_id && (
                            <SectionErrorBoundary section="Dead pack resolution">
                                <DeadPackResolutionPanel userId={parent.user_id} />
                            </SectionErrorBoundary>
                        )}
                        {/* Sachin 2026-03-06: rare-but-scary case — an athlete
                            row that has neither `account_user_id` nor
                            `parent_user_id`. Backend returns parent=null;
                            the FE used to just silently hide every billing
                            widget below, leaving the coach staring at an
                            empty tab. Now we surface a loud "link a parent"
                            banner explaining WHY nothing can be charged. */}
                        {!parent?.user_id && (
                            <div
                                data-testid="athlete-orphan-billing-banner"
                                className="mb-6 border border-amber-600/60 bg-amber-950/30 p-4 rounded"
                                role="alert"
                            >
                                <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 font-semibold mb-1.5">
                                    Not linked to a parent account
                                </div>
                                <p className="text-sm text-zinc-200 leading-relaxed">
                                    <span className="text-white font-medium">{athlete?.first_name || "This athlete"}</span>{" "}
                                    has no parent user account attached. Subscriptions, packs, cards on file, and every other billing
                                    surface live on the parent — so nothing can be charged until a parent is linked.
                                </p>
                                <p className="text-[11px] text-zinc-400 mt-2 leading-relaxed">
                                    Fix: create a parent account for this family (or link an existing one) via{" "}
                                    <span className="text-zinc-200">Owner → Leads → New parent</span>, then reassign this athlete to that account.
                                </p>
                            </div>
                        )}
                        {/* Sachin 2026-03-05: ALWAYS surface the full family
                            subscription list at the top of the athlete's
                            billing tab. Previously only the per-athlete sub
                            was rendered (buried inside BillingPanel), which
                            hid siblings' subs and confused the coach on
                            phone + laptop. This panel lists every active
                            recurring sub on the account with cancel /
                            reassign controls. */}
                        {parent?.user_id && (
                            <SectionErrorBoundary section="Active subscriptions">
                                <ActiveSubscriptionsPanel userId={parent.user_id} />
                            </SectionErrorBoundary>
                        )}
                        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {parent?.user_id && (
                                <SectionErrorBoundary section="Subscribe this athlete">
                                    <div
                                        data-testid="athlete-subscribe-card"
                                        className="border border-zinc-800 bg-zinc-950/60 p-4"
                                    >
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                                            Subscribe this athlete
                                        </div>
                                        <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                                            Subscriptions are per athlete. Pick a pricing block below to start a sub on{" "}
                                            <span className="text-white">{athlete?.first_name || "this athlete"}</span>
                                            . The family card on file pays.
                                        </p>
                                        <AthleteSubscribeWidget
                                            parentUserId={parent.user_id}
                                            athleteId={athleteId}
                                            athleteFirstName={athlete?.first_name}
                                            athleteLastName={athlete?.last_name}
                                            onSubscribed={load}
                                        />
                                    </div>
                                </SectionErrorBoundary>
                            )}
                            {parent?.user_id && (
                                <SectionErrorBoundary section="Charge a session pack">
                                    <div
                                        data-testid="athlete-charge-pack-card"
                                        className="border border-zinc-800 bg-zinc-950/60 p-4"
                                    >
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                                            Charge a session pack
                                        </div>
                                        <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                                            Packs are <span className="text-emerald-300">family-shared</span> — every kid on this account draws from the same pool. The pack purchase lands on the family card on file.
                                        </p>
                                        <ChargeAndSubscribePanel
                                            userId={parent.user_id}
                                            athletes={Array.isArray(parent.athletes) ? parent.athletes : (athlete ? [athlete] : [])}
                                            onChanged={load}
                                        />
                                    </div>
                                </SectionErrorBoundary>
                            )}
                        </div>
                        {parent?.user_id && (
                            <SectionErrorBoundary section="Active packs">
                                <div className="mb-6">
                                    <ActivePacksPanel
                                        userId={parent.user_id}
                                        athletes={Array.isArray(parent.athletes) ? parent.athletes : (athlete ? [athlete] : [])}
                                    />
                                </div>
                            </SectionErrorBoundary>
                        )}
                        {/* Sachin 2026-02-28: per-athlete attendance vs
                            debit reconciliation. Always visible, even
                            when the family has no pack — closes the
                            "I don't see the +/- button" gap. */}
                        {athlete?.athlete_id && (
                            <SectionErrorBoundary section="Session accounting">
                                <SessionAccountingCard
                                    athleteId={athlete.athlete_id}
                                    athleteName={athlete.first_name}
                                />
                            </SectionErrorBoundary>
                        )}
                        <SectionErrorBoundary section="Billing history">
                            <BillingPanel athleteId={athleteId} />
                        </SectionErrorBoundary>
                        {parent?.user_id && (
                            <SectionErrorBoundary section="Commitments">
                                <CommitmentsPanel userId={parent.user_id} />
                            </SectionErrorBoundary>
                        )}
                    </>
                )}
            </div>

            <AssessmentFormModal
                open={assessOpen}
                athlete={athlete}
                currentStatus={statusMeta.label}
                onClose={() => setAssessOpen(false)}
                onSaved={() => { setAssessOpen(false); load(); }}
            />
            {ownerAdminOpen && parent?.user_id && (
                <OwnerUserAdminModal
                    user={parent}
                    onClose={() => setOwnerAdminOpen(false)}
                    onChanged={(result) => {
                        if (result?.deleted) {
                            // User was hard-deleted — bounce back to console
                            navigate("/coach", { replace: true });
                            return;
                        }
                        setOwnerAdminOpen(false);
                        load();
                    }}
                />
            )}
            <AthleteEditModal
                open={athleteEditOpen}
                onClose={() => setAthleteEditOpen(false)}
                athlete={athlete}
                onSaved={() => { setAthleteEditOpen(false); load(); }}
            />
        </div>
    );
}

// ------------- Panels -------------

function SectionCard({ title, children, actions }) {
    return (
        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50 mb-6">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
                <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-200">{title}</h2>
                {actions}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

const InfoRow = ({ icon: Icon, label, value, href, external, title }) => {
    const renderedValue = value || <span className="text-zinc-300">—</span>;
    return (
        <div className="flex items-start gap-3 py-2">
            {Icon && <Icon className="w-4 h-4 text-zinc-300 mt-0.5 flex-shrink-0" />}
            <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-300">{label}</div>
                <div className="text-sm mt-0.5">
                    {href && value ? (
                        <a
                            href={href}
                            title={title}
                            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            className="text-zinc-100 hover:text-brand-red underline-offset-2 hover:underline transition-colors break-all"
                        >
                            {value}
                        </a>
                    ) : renderedValue}
                </div>
            </div>
        </div>
    );
};

function WaiverNotSigned({ parentUserId }) {
    const [busy, setBusy] = useState(false);
    const onResend = async () => {
        if (!parentUserId) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/waivers/resend`,
                { user_id: parentUserId },
                { withCredentials: true },
            );
            if (r.data?.sent) toast.success(`Waiver email sent to ${r.data.to}`);
            else if (r.data?.skipped_reason === "throttled") toast.warning(r.data.message);
            else if (r.data?.skipped_reason === "already_signed") toast.info(r.data.message);
            else toast.info(r.data?.message || "Skipped");
        } catch (e) {
            toast.error("Couldn't send waiver email", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="space-y-2">
            <div className="text-sm text-amber-300">Waiver not yet signed.</div>
            <button
                type="button"
                data-testid="dossier-resend-waiver-btn"
                onClick={onResend}
                disabled={busy || !parentUserId}
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white transition-colors disabled:opacity-50"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                Resend waiver email
            </button>
        </div>
    );
}


function OverviewPanel({ athlete, parent, onAthleteRefresh }) {
    const composeUrl = useGmailComposeUrl();
    const waiver = parent?.waiver;
    const addr = parent?.address || {};
    const ec = parent?.emergency_contact || {};
    const [sendingLink, setSendingLink] = useState(false);
    const sendWelcomeLink = async () => {
        if (!parent?.user_id || !athlete?.athlete_id) return;
        if (!athlete?.email) {
            toast.error("Add an athlete email first", {
                description: "Use \"Edit athlete\" to set their email — then come back and send the welcome link.",
            });
            return;
        }
        setSendingLink(true);
        try {
            const r = await axios.post(
                `${API}/coach/clients/${parent.user_id}/athletes/${athlete.athlete_id}/send-welcome-link`,
                {},
                { withCredentials: true },
            );
            const cc = r.data?.cc?.length ? ` (parent CC'd: ${r.data.cc.join(", ")})` : "";
            toast.success("Welcome link sent", {
                description: `${r.data?.to || athlete.email} got a one-tap sign-in email.${cc}`,
            });
            onAthleteRefresh?.();
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Unknown error";
            toast.error("Couldn't send welcome link", { description: String(detail) });
        } finally {
            setSendingLink(false);
        }
    };
    return (
        <div className="grid md:grid-cols-2 gap-6">
            <SectionCard title="Athlete">
                <InfoRow label="Name" value={`${athlete.first_name} ${athlete.last_name}`} />
                <InfoRow label="DOB" value={athlete.dob || "—"} />
                <InfoRow label="Age" value={calcAge(athlete.dob) ?? "—"} />
                <InfoRow label="Gender" value={athlete.gender || "—"} />
                <InfoRow label="Track" value={TRACK_LABELS[athlete.track] || athlete.track} />
                {athlete.grade && <InfoRow label="Grade" value={GRADE_LABELS[athlete.grade] || athlete.grade} />}
                {((athlete.sports || []).length > 0 || athlete.sports_other) && (
                    <InfoRow
                        label="Sport(s)"
                        value={[
                            ...(athlete.sports || []).map((s) => SPORT_LABELS[s] || s),
                            athlete.sports_other ? `Other: ${athlete.sports_other}` : null,
                        ].filter(Boolean).join(", ")}
                    />
                )}
                <InfoRow label="Athlete email" value={athlete.email || "—"} href={athlete.email ? composeUrl(athlete.email) : undefined} external={!!athlete.email} title={athlete.email ? composeUrl.title : undefined} />
                {/* Phase C login surface (Sachin 2026-02-27): when the athlete
                    has no email, nudge the coach to add one. When they DO,
                    let the coach fire a welcome magic-link on demand. */}
                {!athlete.email ? (
                    <div
                        data-testid="athlete-add-login-banner"
                        className="mt-2 border border-amber-700/40 bg-amber-900/15 px-3 py-2.5"
                    >
                        <div className="text-[10px] tracking-[0.2em] uppercase text-amber-300 font-semibold mb-1">
                            No athlete login yet
                        </div>
                        <p className="text-[11px] text-amber-100/80 leading-snug">
                            {athlete.first_name || "This athlete"} doesn&apos;t have their own login. Add an email on the <span className="text-white">Edit athlete</span> screen above — once saved, you can send a one-tap welcome link from here.
                        </p>
                    </div>
                ) : (
                    <button
                        type="button"
                        data-testid="athlete-send-welcome-link"
                        onClick={sendWelcomeLink}
                        disabled={sendingLink}
                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-brand-red hover:text-white border border-zinc-800 hover:border-brand-red px-3 py-1.5 transition-colors disabled:opacity-50"
                        title={`Send a one-tap sign-in email to ${athlete.email}. Parent CC'd.`}
                    >
                        {sendingLink ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                        {sendingLink ? "Sending…" : "Send welcome link"}
                    </button>
                )}
                {athlete.health_notes && <InfoRow icon={Heart} label="Health notes" value={athlete.health_notes} />}
            </SectionCard>

            <SectionCard title="Parent / Guardian">
                <InfoRow icon={User} label="Name" value={parent?.name || "—"} />
                <InfoRow icon={Mail} label="Email" value={parent?.email || "—"} href={parent?.email ? composeUrl(parent.email) : undefined} external={!!parent?.email} title={parent?.email ? composeUrl.title : undefined} />
                <InfoRow icon={Phone} label="Phone" value={parent?.phone || "—"} href={parent?.phone ? `tel:${parent.phone}` : undefined} />
                {(addr.street || addr.city) && (
                    <InfoRow icon={MapPin} label="Address" value={`${addr.street || ""} ${addr.city || ""}${addr.state ? ", " + addr.state : ""} ${addr.zip || ""}`.trim()} />
                )}
                <InfoRow label="Signup type" value={parent?.signup_for || "—"} />
            </SectionCard>

            <SectionCard title="Waiver">
                {waiver?.signed_at ? (
                    <>
                        <InfoRow label="Signed by" value={waiver.signed_name} />
                        <InfoRow label="Version" value={waiver.version} />
                        <InfoRow label="Signed on" value={new Date(waiver.signed_at).toLocaleString()} />
                    </>
                ) : (
                    <WaiverNotSigned parentUserId={parent?.user_id} />
                )}
            </SectionCard>

            <SectionCard title="Emergency contact">
                <InfoRow label="Name" value={ec.name || "—"} />
                <InfoRow label="Relationship" value={ec.relationship || "—"} />
                <InfoRow label="Phone" value={ec.phone || "—"} />
            </SectionCard>
        </div>
    );
}

function AssessmentPanel({ assessment, onEdit }) {
    if (!assessment) {
        return (
            <SectionCard title="Assessment" actions={<button type="button" onClick={onEdit} className="btn-tactical text-xs">Run now</button>}>
                <p className="text-zinc-300 text-sm">No assessment recorded yet. Run one to capture baseline numbers + movement screen.</p>
            </SectionCard>
        );
    }
    const speed = assessment.speed || {};
    const mv = assessment.movement || {};
    const moveEntries = Object.entries(mv).filter(([, v]) => (v || "").trim());
    return (
        <>
            {assessment.include_speed && (
                <SectionCard title="Speed & Power" actions={<button type="button" onClick={onEdit} className="btn-ghost-tactical text-xs">Edit</button>}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <Stat label="Shredmill speed" value={speed.shredmill_speed} />
                        <Stat label="Shredmill resistance" value={speed.shredmill_resistance} />
                        <Stat label="10-yd dash" value={speed.ten_yd} suffix="s" />
                        <Stat label="20-yd dash" value={speed.twenty_yd} suffix="s" />
                        <Stat label="Approach jump" value={speed.approach_jump} suffix="in" />
                        <Stat label="Vertical jump" value={speed.vertical_jump} suffix="in" />
                    </div>
                </SectionCard>
            )}
            <SectionCard title="Movement screen">
                {moveEntries.length === 0 ? (
                    <p className="text-sm text-zinc-300">No movement notes recorded.</p>
                ) : (
                    <div className="space-y-3">
                        {moveEntries.map(([k, v]) => (
                            <div key={k} className="border-l-2 border-brand-red pl-3">
                                <div className="text-[11px] tracking-wider uppercase text-zinc-300">{k}</div>
                                <div className="text-sm mt-0.5 whitespace-pre-wrap">{v}</div>
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>
            {assessment.overall_notes && (
                <SectionCard title="Overall notes">
                    <p className="text-sm whitespace-pre-wrap">{assessment.overall_notes}</p>
                </SectionCard>
            )}
            <p className="text-[11px] text-zinc-300">Last updated {new Date(assessment.updated_at).toLocaleString()}</p>
        </>
    );
}

const Stat = ({ label, value, suffix }) => (
    <div className="border border-zinc-800 rounded-lg p-3">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">{label}</div>
        <div className="mt-1 font-display text-2xl tracking-wider">
            {value !== null && value !== undefined ? `${value}${suffix ? ` ${suffix}` : ""}` : <span className="text-zinc-400">—</span>}
        </div>
    </div>
);

function SessionsPanel({ upcoming, past }) {
    return (
        <>
            <SectionCard title={`Upcoming (${upcoming.length})`}>
                {upcoming.length === 0 ? (
                    <p className="text-sm text-zinc-300">No upcoming bookings.</p>
                ) : (
                    <ul className="divide-y divide-zinc-800">
                        {upcoming.map((b) => <SessionRow key={b.booking_id} b={b} />)}
                    </ul>
                )}
            </SectionCard>
            <SectionCard title={`Past (${past.length})`}>
                {past.length === 0 ? (
                    <p className="text-sm text-zinc-300">No past sessions.</p>
                ) : (
                    <ul className="divide-y divide-zinc-800">
                        {past.slice(0, 40).map((b) => <SessionRow key={b.booking_id} b={b} past />)}
                    </ul>
                )}
            </SectionCard>
        </>
    );
}

const SessionRow = ({ b, past }) => (
    <li className="py-3 flex items-center justify-between gap-4">
        <div>
            <div className="text-sm font-semibold">
                {new Date(`${b.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                {" · "}{formatTimeDisplay(b.start_time)}
            </div>
            <div className="text-xs text-zinc-300 mt-0.5">
                {b.label || `${b.duration_minutes}min`}{b.category ? ` · ${b.category}` : ""}
            </div>
        </div>
        <span className={`text-[10px] tracking-wider uppercase px-2 py-1 border ${
            b.status === "cancelled" ? "border-zinc-700 text-zinc-300"
            : past ? "border-brand-navy-700 text-brand-navy-200"
            : "border-brand-red text-brand-red"
        }`}>
            {b.status === "cancelled" ? "Cancelled" : past ? "Attended" : "Booked"}
        </span>
    </li>
);


/**
 * AttendancePanel — per-athlete attendance log + counts.
 * Hits /coach/attendance/athlete/{id}?days=90. Renders summary tiles
 * (attended / no-show / late-cancel / cancelled-on-time / scheduled)
 * plus a chronological log of every booking in the window.
 */
function AttendancePanel({ athleteId, athlete }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [windowDays, setWindowDays] = useState(3650);
    const [deletingId, setDeletingId] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const canDelete = ["owner", "coach"].includes(user?.role);
    // "+ Log past session" lives on the Schedule tab now (Sachin 2026-02-27);
    // attendance is read-only — past sessions get logged where the coach
    // arranges new ones.

    useEffect(() => {
        if (!athleteId) return;
        let alive = true;
        (async () => {
            setBusy(true);
            try {
                const r = await axios.get(
                    `${API}/coach/attendance/athlete/${athleteId}?days=${windowDays}`,
                    { withCredentials: true },
                );
                if (alive) setData(r.data);
            } catch (e) {
                toast.error("Couldn't load attendance", { description: e?.response?.data?.detail });
            } finally {
                if (alive) setBusy(false);
            }
        })();
        return () => { alive = false; };
    }, [athleteId, windowDays, reloadTick]);

    // Delete a manual log. Two-step confirm:
    //   1. Confirm the destructive action with date/time context.
    //   2. Ask whether to credit the session back to the pack.
    // Default "credit back" = YES because the most common reason to
    // delete is cleaning up a test entry or accidental double-log.
    const handleDeleteManualLog = async (row) => {
        const dateLabel = new Date(`${row.date}T00:00:00Z`).toLocaleDateString(
            undefined,
            { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" },
        );
        if (!window.confirm(`Delete this manual log of ${dateLabel} at ${row.start_time || "—"}? This can't be undone.`)) {
            return;
        }
        const creditBack = window.confirm(
            "Credit a session back to the pack?\n\n" +
            "OK = credit it back (most common when cleaning up test entries or double-logs).\n" +
            "Cancel = leave the pack as-is (use when the athlete really attended but you just want to delete the entry).",
        );
        setDeletingId(row.booking_id);
        try {
            const r = await axios.delete(
                `${API}/coach/attendance/record/${row.booking_id}?credit_back=${creditBack}`,
                { withCredentials: true },
            );
            const refund = r.data?.refunded_from;
            if (creditBack && refund) {
                toast.success("Manual log deleted", {
                    description: `1 session credited back to ${refund.label} · ${refund.remaining} left.`,
                });
            } else if (creditBack && !refund) {
                toast.success("Manual log deleted", {
                    description: "No matching pack deduction found — nothing to credit back.",
                });
            } else {
                toast.success("Manual log deleted", { description: "Pack untouched." });
            }
            setReloadTick((t) => t + 1);
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        } finally {
            setDeletingId(null);
        }
    };

    // Reverse a scheduled check-in (auto or manual scan). Silent refund —
    // no email goes to the athlete. Two-step confirm:
    //   1. Confirm the destructive action with date/time context.
    //   2. Ask whether to credit the session back to the pack.
    // Default "credit back" = YES because the most common reason to reverse
    // is "athlete didn't actually show up but the auto-checkin already fired."
    const handleReverseScheduledCheckin = async (row) => {
        const dateLabel = new Date(`${row.date}T00:00:00Z`).toLocaleDateString(
            undefined,
            { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" },
        );
        if (!window.confirm(`Reverse this ${row.auto_checked_in ? "auto-" : ""}check-in for ${dateLabel} at ${row.start_time || "—"}?`)) {
            return;
        }
        const creditBack = window.confirm(
            "Credit the session back to the pack?\n\n" +
            "OK = credit it back (most common — athlete didn't actually show).\n" +
            "Cancel = leave the pack as-is.",
        );
        setDeletingId(row.booking_id);
        try {
            const r = await axios.delete(
                `${API}/coach/attendance/record/${row.booking_id}?credit_back=${creditBack}`,
                { withCredentials: true },
            );
            const refund = r.data?.refunded_from;
            if (creditBack && refund) {
                toast.success("Check-in reversed", {
                    description: `1 session credited back to ${refund.label} · ${refund.remaining} left.`,
                });
            } else if (creditBack && !refund) {
                toast.success("Check-in reversed", {
                    description: "No matching pack deduction found — nothing to credit back.",
                });
            } else {
                toast.success("Check-in reversed", { description: "Pack untouched." });
            }
            setReloadTick((t) => t + 1);
        } catch (e) {
            toast.error("Couldn't reverse", { description: e?.response?.data?.detail });
        } finally {
            setDeletingId(null);
        }
    };

    if (busy) {
        return <div className="text-zinc-500 text-sm py-6 px-4">Loading attendance…</div>;
    }
    if (!data) return null;

    const counts = data.counts || {};
    // Sachin 2026-03-05: "I don't like attendance and scheduled being
    // mixed together. Please have scheduled and attendance in different
    // tabs." Scheduled (upcoming/booked) sessions live on the Schedule
    // tab. Attendance is strictly what HAPPENED.
    const tiles = [
        { label: "Attended", value: counts.attended || 0, color: "text-emerald-300", border: "border-emerald-700/40" },
        { label: "No-shows", value: counts.no_show || 0, color: "text-rose-400", border: "border-rose-700/40" },
        { label: "Late cancels", value: counts.late_cancel || 0, color: "text-amber-400", border: "border-amber-700/40" },
        { label: "On-time cancels", value: counts.cancelled_on_time || 0, color: "text-zinc-300", border: "border-zinc-700" },
    ];

    // Same rule for the log below the tiles — only actual outcomes,
    // never upcoming scheduled rows.
    const attendanceLog = (data.log || []).filter((r) => r.outcome !== "scheduled");

    return (
        <div data-testid="dossier-attendance-panel" className="space-y-6 px-4 sm:px-6 py-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display text-2xl text-white">Attendance</h2>
                    <p className="text-xs text-zinc-500 mt-1">{windowDays >= 3650 ? "All time" : `Last ${windowDays} days`} · auto-checked-in sessions count as attended.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        data-testid="attendance-window-select"
                        value={windowDays}
                        onChange={(e) => setWindowDays(Number(e.target.value))}
                        className="bg-black border border-zinc-800 px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase text-zinc-200 focus:border-brand-red outline-none"
                    >
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={365}>Last year</option>
                        <option value={3650}>All time</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {tiles.map((t) => (
                    <div
                        key={t.label}
                        data-testid={`attendance-tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`p-4 border ${t.border} bg-zinc-950/50 rounded`}
                    >
                        <div className={`font-display text-3xl ${t.color}`}>{t.value}</div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mt-1">{t.label}</div>
                    </div>
                ))}
            </div>

            {attendanceLog.length === 0 ? (
                <p className="text-sm text-zinc-500" data-testid="attendance-empty">
                    No completed sessions in this window. Upcoming sessions live on the <span className="text-zinc-300">Schedule</span> tab.
                </p>
            ) : (
                <div className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-900/50 text-[10px] tracking-wider uppercase text-zinc-300">
                            <tr>
                                <th className="text-left px-4 py-3">Date</th>
                                <th className="text-left px-4 py-3">Time</th>
                                <th className="text-left px-4 py-3">Session</th>
                                <th className="text-left px-4 py-3">Outcome</th>
                                {canDelete && <th className="text-right px-4 py-3 w-12"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {attendanceLog.map((r) => {
                                const outcomeMeta = {
                                    attended: { label: r.auto_checked_in ? "Attended · auto" : "Attended", color: "text-emerald-300" },
                                    no_show: { label: "No-show", color: "text-rose-400" },
                                    late_cancel: { label: "Late cancel", color: "text-amber-400" },
                                    cancelled_on_time: { label: "Cancelled (on time)", color: "text-zinc-400" },
                                    scheduled: { label: "Scheduled", color: "text-brand-navy-300" },
                                }[r.outcome] || { label: r.outcome, color: "text-zinc-300" };
                                return (
                                    <tr key={r.booking_id} data-testid={`attendance-log-${r.booking_id}`} className="border-t border-zinc-900">
                                        <td className="px-4 py-3 text-zinc-200 whitespace-nowrap">
                                            {new Date(`${r.date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-200">{r.start_time}</td>
                                        <td className="px-4 py-3 text-white">{r.label || r.category || "Session"}</td>
                                        <td className={`px-4 py-3 text-[11px] tracking-wider uppercase ${outcomeMeta.color}`}>
                                            {outcomeMeta.label}
                                            {r.cancelled_at && (r.outcome === "cancelled_on_time" || r.outcome === "late_cancel") && (
                                                <div data-testid={`attendance-cancelled-at-${r.booking_id}`} className="text-[10px] normal-case tracking-normal text-zinc-500 mt-0.5">
                                                    {new Date(r.cancelled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                                </div>
                                            )}
                                        </td>
                                        {canDelete && (
                                            <td className="px-4 py-3 text-right">
                                                {r.is_manual_log ? (
                                                    <button
                                                        type="button"
                                                        data-testid={`attendance-delete-${r.booking_id}`}
                                                        onClick={() => handleDeleteManualLog(r)}
                                                        disabled={deletingId === r.booking_id}
                                                        className="text-zinc-500 hover:text-rose-400 disabled:opacity-40 transition-colors p-1.5 -m-1.5"
                                                        title="Delete this manual log"
                                                        aria-label="Delete manual log"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                ) : r.outcome === "attended" ? (
                                                    <button
                                                        type="button"
                                                        data-testid={`attendance-reverse-${r.booking_id}`}
                                                        onClick={() => handleReverseScheduledCheckin(r)}
                                                        disabled={deletingId === r.booking_id}
                                                        className="text-zinc-500 hover:text-rose-400 disabled:opacity-40 transition-colors p-1.5 -m-1.5"
                                                        title={r.auto_checked_in ? "Reverse this auto check-in (refunds session)" : "Reverse this check-in (refunds session)"}
                                                        aria-label="Reverse check-in"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                ) : null}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function WorkoutsPanel({ sessions, maxes, athleteId, userId, athleteFirstName, onMaxUpdated }) {
    const { user } = useAuth();
    const canEdit = ["owner", "coach"].includes(user?.role);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    const startEdit = (m) => { setEditingId(m.exercise_id); setDraft(String(m.value || "")); };
    const cancel = () => { setEditingId(null); setDraft(""); };
    const save = async (m) => {
        const v = parseInt(draft, 10);
        if (!v || v <= 0) { toast.error("Enter a positive number"); return; }
        setSaving(true);
        try {
            await axios.put(
                `${API}/coach/athletes/${athleteId}/maxes`,
                { user_id: userId, exercise_id: m.exercise_id, value: v },
                { withCredentials: true },
            );
            toast.success("Updated");
            cancel();
            onMaxUpdated?.();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {canEdit && (
                <SectionCard title="Tablet mode">
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-200">
                                Hand the kid a tablet and drive a workout together. Their plates, rest timers, and 1RM auto-progression run locally — siblings won't share numbers.
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="run-on-tablet-btn"
                            onClick={() => setPickerOpen(true)}
                            className="btn-tactical inline-flex"
                        >
                            <Play className="w-4 h-4" /> Run on tablet
                        </button>
                    </div>
                </SectionCard>
            )}
            <SectionCard title={`1 Rep Maxes (${maxes.length})`}>
                {maxes.length === 0 ? (
                    <p className="text-sm text-zinc-300">No maxes recorded yet.</p>
                ) : (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {maxes.map((m) => {
                            const isEditing = editingId === m.exercise_id;
                            return (
                                <div key={m.exercise_id} data-testid={`max-card-${m.exercise_id}`} className="border border-zinc-800 rounded-lg p-3">
                                    <div className="text-[10px] tracking-wider uppercase text-zinc-300 truncate">{m.exercise_name}</div>
                                    {isEditing ? (
                                        <div className="flex items-center gap-2 mt-2">
                                            <input
                                                data-testid={`max-input-${m.exercise_id}`}
                                                type="number"
                                                value={draft}
                                                onChange={(e) => setDraft(e.target.value)}
                                                autoFocus
                                                className="w-20 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm focus:outline-none focus:border-brand-red"
                                            />
                                            <span className="text-xs text-zinc-400">lbs</span>
                                            <button type="button" data-testid={`save-max-${m.exercise_id}`} onClick={() => save(m)} disabled={saving} className="text-[10px] tracking-[0.15em] uppercase text-brand-red hover:underline disabled:opacity-50">{saving ? "…" : "Save"}</button>
                                            <button type="button" onClick={cancel} className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 hover:text-white">Cancel</button>
                                        </div>
                                    ) : (
                                        <div className="flex items-baseline gap-1.5 mt-1">
                                            <span className="font-display text-2xl tracking-wider">{m.value}</span>
                                            <span className="text-xs text-zinc-300">lbs</span>
                                            {canEdit && (
                                                <button type="button" data-testid={`edit-max-${m.exercise_id}`} onClick={() => startEdit(m)} className="ml-auto text-[10px] tracking-[0.15em] uppercase text-zinc-400 hover:text-brand-red">Edit</button>
                                            )}
                                        </div>
                                    )}
                                    <div className="text-[10px] text-zinc-400 mt-1">
                                        {m.source === "auto_progression" ? "Auto-progressed" : m.updated_via === "coach_dossier" ? "Coach edit" : "Manual"} · {new Date(m.updated_at).toLocaleDateString()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </SectionCard>
            <SectionCard title={`Completed workouts (${sessions.length})`}>
                {sessions.length === 0 ? (
                    <p className="text-sm text-zinc-300">No workouts completed yet.</p>
                ) : (
                    <ul className="divide-y divide-zinc-800">
                        {sessions.slice(0, 30).map((s) => (
                            <li key={s.session_id || s.completed_at} className="py-2.5 flex justify-between text-sm">
                                <span>{s.workout_title || s.workout_id}</span>
                                <span className="text-xs text-zinc-300">{new Date(s.completed_at).toLocaleDateString()}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </SectionCard>
            {pickerOpen && (
                <RunWorkoutPickerModal
                    athleteId={athleteId}
                    athleteName={athleteFirstName || "Athlete"}
                    open={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                />
            )}
        </>
    );
}

function BillingPanel({ athleteId }) {
    const { user } = useAuth();
    const isOwner = user?.role === "owner";
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [refundingId, setRefundingId] = useState(null);
    const [showEnroll, setShowEnroll] = useState(false);
    const [showCardSave, setShowCardSave] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/athletes/${athleteId}/billing`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            toast.error("Couldn't load billing", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/athletes/${athleteId}/billing`, { withCredentials: true });
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) toast.error("Couldn't load billing", { description: e?.response?.data?.detail });
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [athleteId]);

    const refundInvoice = async (inv) => {
        const fmt = (cents, cur) => new Intl.NumberFormat("en-US", { style: "currency", currency: (cur || "usd").toUpperCase() }).format((cents || 0) / 100);
        const amount = fmt(inv.amount_paid || inv.amount_due, inv.currency);
        const reason = window.prompt(
            `Refund ${amount} back to this customer? This is a full refund and cannot be undone.\n\nOptional reason (for the audit log):`,
            "",
        );
        if (reason === null) return; // cancel
        setRefundingId(inv.id);
        try {
            const r = await axios.post(
                `${API}/coach/billing/invoices/${inv.id}/refund`,
                { invoice_id: inv.id, reason },
                { withCredentials: true },
            );
            toast.success(`Refunded ${fmt(r.data.amount_cents, inv.currency)}`);
            await load();
        } catch (e) {
            toast.error("Refund failed", { description: e?.response?.data?.detail });
        } finally {
            setRefundingId(null);
        }
    };

    if (busy) return <SectionCard title="Billing"><div className="flex items-center gap-2 text-zinc-300 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></SectionCard>;

    const sub = data?.subscription;
    const invoices = data?.invoices || [];
    const fmt = (cents, cur) => new Intl.NumberFormat("en-US", { style: "currency", currency: (cur || "usd").toUpperCase() }).format((cents || 0) / 100);

    return (
        <>
            {/* Card on file — ALWAYS visible (2026-02-13, Sachin spec).
                Shows current saved card + dual-action: "Open here" lets the
                coach enter the parent's card from the gym counter via Stripe
                Checkout in a new tab; "Email link" emails the parent a
                Stripe-hosted setup form. Same endpoints as the parent's
                Lead Detail billing panel. Hidden only if the athlete has no
                resolvable parent account (legacy data). */}
            {data?.parent_user_id && (
                <SectionCard title="Card on file — for charges">
                    <SetupCardForClientButton
                        clientUserId={data.parent_user_id}
                        clientEmail={data.parent_email}
                        clientName={data.parent_name}
                    />
                </SectionCard>
            )}
            <SectionCard title="Subscription — this athlete">
                {!sub ? (
                    <div>
                        <p className="text-sm text-zinc-300" data-testid="athlete-sub-status-none">
                            No subscription for this athlete.
                        </p>
                        {(data?.siblings_subscribed || 0) > 0 && (
                            <p className="text-xs text-zinc-500 mt-2" data-testid="athlete-sub-sibling-note">
                                {data.siblings_subscribed} other athlete{data.siblings_subscribed > 1 ? "s" : ""} on this account {data.siblings_subscribed > 1 ? "have" : "has"} a live subscription.
                            </p>
                        )}
                        <div className="mt-4 flex flex-wrap gap-2">
                            {data?.parent_user_id && (
                                <Link
                                    to={`/coach/leads/${data.parent_user_id}?tab=billing`}
                                    data-testid="open-subscribe-from-block"
                                    className="btn-tactical text-xs"
                                >
                                    Subscribe from a block →
                                </Link>
                            )}
                            <button
                                type="button"
                                data-testid="open-send-enrollment"
                                onClick={() => setShowEnroll(true)}
                                className="btn-tactical text-xs"
                            >
                                <Mail className="w-3.5 h-3.5" /> Send enrollment link
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="font-display text-2xl tracking-wider">{sub.plan_name || "Plan"}</div>
                        <div className="mt-2 text-[10px] tracking-wider uppercase">
                            <span
                                data-testid="athlete-sub-status-chip"
                                className={`px-2 py-1 border ${sub.status === "active" || sub.status === "trialing" ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}
                            >
                                {sub.status === "active" || sub.status === "trialing" ? "Subscribed · live" : sub.status}{sub.cancel_at_period_end ? " · cancelling" : ""}
                            </span>
                        </div>
                        {sub.current_period_end && (
                            <div className="text-xs text-zinc-300 mt-3">
                                {sub.cancel_at_period_end ? "Ends" : "Renews"}{" "}
                                {new Date(sub.current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                            </div>
                        )}
                    </div>
                )}
            </SectionCard>
            <SectionCard title={`Invoices (${invoices.length})`}>
                {invoices.length === 0 ? (
                    <p className="text-sm text-zinc-300">No invoices yet.</p>
                ) : (
                    <ul className="divide-y divide-zinc-800">
                        {invoices.map((inv) => {
                            const refundable = isOwner && inv.status === "paid" && !inv.refunded;
                            return (
                                <li key={inv.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                                    <div>
                                        <div>{fmt(inv.amount_paid || inv.amount_due, inv.currency)}{inv.refunded && <span className="ml-2 text-[10px] tracking-[0.15em] uppercase text-amber-300 border border-amber-500/40 px-1.5 py-0.5">refunded</span>}</div>
                                        <div className="text-xs text-zinc-300">{new Date(inv.created * 1000).toLocaleDateString()} · <span className={inv.status === "paid" ? "text-brand-navy-300" : "text-amber-400"}>{inv.status}</span></div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {refundable && (
                                            <button
                                                type="button"
                                                data-testid={`refund-invoice-${inv.id}`}
                                                onClick={() => refundInvoice(inv)}
                                                disabled={refundingId === inv.id}
                                                className="text-[11px] tracking-[0.15em] uppercase px-2.5 py-1 border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                                            >
                                                {refundingId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                                Refund
                                            </button>
                                        )}
                                        {inv.hosted_invoice_url && (
                                            <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-white inline-flex items-center gap-1 text-xs">
                                                View <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </SectionCard>
            {data?.parent_user_id && (
                <PaymentHistoryPanel userId={data.parent_user_id} />
            )}
            {showEnroll && (
                <EnrollmentLinkModal
                    athleteId={athleteId}
                    onClose={() => setShowEnroll(false)}
                />
            )}
            {showCardSave && (
                <CardSaveLinkModal
                    athleteId={athleteId}
                    onClose={() => setShowCardSave(false)}
                />
            )}
        </>
    );
}


function CardSaveLinkModal({ athleteId, onClose }) {
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);
    const [parentUserId, setParentUserId] = useState(null);
    const [parentName, setParentName] = useState("");
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const d = await axios.get(`${API}/coach/athletes/${athleteId}`, { withCredentials: true });
                if (cancelled) return;
                setParentUserId(d.data?.parent?.user_id);
                setParentName(d.data?.parent?.name || d.data?.parent?.email || "");
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [athleteId]);

    const send = async () => {
        if (!parentUserId) { toast.error("No parent account found"); return; }
        setSending(true);
        try {
            await axios.post(
                `${API}/coach/clients/${parentUserId}/send-card-save-link`,
                { custom_note: note },
                { withCredentials: true },
            );
            toast.success("Card-save link sent", { description: parentName });
            onClose?.();
        } catch (e) {
            toast.error("Couldn't send", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="card-save-modal" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md">
                <div className="px-5 py-3 border-b border-zinc-800">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Card on file</div>
                    <h4 className="font-display text-xl tracking-wider mt-0.5">Save card · $0 charge</h4>
                </div>
                <div className="p-5 space-y-4">
                    {busy ? (
                        <div className="inline-flex items-center gap-2 text-zinc-300 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                    ) : (
                        <>
                            <p className="text-sm text-zinc-300">
                                Sending to <span className="text-white font-semibold">{parentName}</span>. They'll get an email with a one-tap link to a Stripe-hosted form. No money moves — the card is just saved so you can charge it later from the Charge button.
                            </p>
                            <div>
                                <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1 block">Note to client (optional)</label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={3}
                                    placeholder="e.g. 'For drop-ins and late-cancel fees only'"
                                    data-testid="card-save-note"
                                    className="w-full bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="text-xs px-4 py-2 text-zinc-300 hover:text-white">Cancel</button>
                    <button
                        type="button"
                        onClick={send}
                        disabled={sending || busy || !parentUserId}
                        data-testid="card-save-send-btn"
                        className="text-xs px-4 py-2 bg-brand-red text-white disabled:opacity-40 inline-flex items-center gap-2"
                    >
                        {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                        Email link
                    </button>
                </div>
            </div>
        </div>
    );
}


function CommitmentsPanel({ userId }) {
    const [busy, setBusy] = useState(true);
    const [commitments, setCommitments] = useState([]);
    const [rollover, setRollover] = useState({ credits: [], available_cents: 0 });
    const [actingId, setActingId] = useState(null);
    const [repriceQuote, setRepriceQuote] = useState(null); // {commitment, quote}

    const load = async () => {
        setBusy(true);
        try {
            const [c, r] = await Promise.all([
                axios.get(`${API}/coach/clients/${userId}/commitments`, { withCredentials: true }),
                axios.get(`${API}/coach/clients/${userId}/rollover-credits`, { withCredentials: true }),
            ]);
            setCommitments(c.data?.commitments || []);
            setRollover(r.data || { credits: [], available_cents: 0 });
        } catch {
            toast.error("Couldn't load commitments");
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { if (userId) load(); }, [userId]);

    const forceStart = async (c) => {
        if (!window.confirm(`Force-start commitment for ${c.display_name}? Commitment window will begin NOW (instead of waiting for the first check-in).`)) return;
        setActingId(c.commitment_id);
        try {
            await axios.post(`${API}/coach/commitments/${c.commitment_id}/start`, {}, { withCredentials: true });
            toast.success("Commitment started");
            load();
        } catch (e) {
            toast.error("Couldn't start", { description: e?.response?.data?.detail });
        } finally {
            setActingId(null);
        }
    };

    const cancel = async (c) => {
        const reason = window.prompt(`Cancel commitment "${c.display_name}"? Type a reason (optional):`, "");
        if (reason === null) return;
        setActingId(c.commitment_id);
        try {
            const r = await axios.post(
                `${API}/coach/commitments/${c.commitment_id}/cancel`,
                { reason: reason || "" },
                { withCredentials: true },
            );
            if (r.data?.reprice_quote?.applicable) {
                setRepriceQuote({ commitment: c, quote: r.data.reprice_quote });
                toast.warning("Commitment cancelled — reprice quote ready");
            } else {
                toast.success("Commitment cancelled");
            }
            load();
        } catch (e) {
            toast.error("Couldn't cancel", { description: e?.response?.data?.detail });
        } finally {
            setActingId(null);
        }
    };

    const chargeReprice = async () => {
        const q = repriceQuote;
        if (!q) return;
        if (!window.confirm(`Charge $${(q.quote.total_cents/100).toFixed(2)} (incl 10.3% tax) on saved card for early-cancel reprice?`)) return;
        try {
            await axios.post(
                `${API}/coach/commitments/${q.commitment.commitment_id}/charge-reprice`,
                {},
                { withCredentials: true },
            );
            toast.success("Reprice charged");
            setRepriceQuote(null);
            load();
        } catch (e) {
            toast.error("Charge failed", { description: e?.response?.data?.detail });
        }
    };

    if (busy) return <SectionCard title="Commitments"><div className="flex items-center gap-2 text-zinc-300 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></SectionCard>;

    return (
        <>
            <SectionCard title={`Membership commitments (${commitments.length})`}>
                {commitments.length === 0 ? (
                    <p className="text-sm text-zinc-400">No commitments on file. Send an enrollment link for a member pack with a commitment to track one here.</p>
                ) : (
                    <div className="space-y-2" data-testid="commitments-list">
                        {commitments.map((c) => (
                            <div key={c.commitment_id} data-testid={`commitment-${c.commitment_id}`} className="border border-zinc-800 rounded-lg px-3 py-2 bg-zinc-950 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate">{c.display_name}</div>
                                    <div className="text-[11px] text-zinc-500 mt-0.5 flex flex-wrap gap-x-3">
                                        <span>{c.session_count}-session · {c.commitment_weeks}wk commit</span>
                                        <span>${(c.amount_paid_cents/100).toFixed(2)}</span>
                                        <span className={`uppercase tracking-wider ${
                                            c.status === "active" ? "text-emerald-400" :
                                            c.status === "pending" ? "text-amber-300" :
                                            c.status === "completed" ? "text-zinc-400" : "text-red-400"
                                        }`}>{c.status}</span>
                                        {c.commitment_started_at && <span>Started {new Date(c.commitment_started_at).toLocaleDateString()}</span>}
                                        {c.commitment_ends_at && <span>Ends {new Date(c.commitment_ends_at).toLocaleDateString()}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {c.status === "pending" && (
                                        <button
                                            type="button"
                                            onClick={() => forceStart(c)}
                                            disabled={actingId === c.commitment_id}
                                            data-testid={`commit-start-${c.commitment_id}`}
                                            className="text-[11px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 disabled:opacity-50"
                                        >Start now</button>
                                    )}
                                    {(c.status === "pending" || c.status === "active") && (
                                        <button
                                            type="button"
                                            onClick={() => cancel(c)}
                                            disabled={actingId === c.commitment_id}
                                            data-testid={`commit-cancel-${c.commitment_id}`}
                                            className="text-[11px] px-2 py-1 rounded bg-red-900/40 hover:bg-red-900/60 text-red-200 disabled:opacity-50"
                                        >Cancel</button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>

            <SectionCard title={`Rollover credits ($${(rollover.available_cents/100).toFixed(2)} available)`}>
                {rollover.credits.length === 0 ? (
                    <p className="text-sm text-zinc-400">No rollover credits yet. Credits are banked at 85% of unused session value when a commitment completes.</p>
                ) : (
                    <div className="space-y-2" data-testid="rollover-list">
                        {rollover.credits.map((r) => (
                            <div key={r.rollover_credit_id} className="border border-zinc-800 rounded-lg px-3 py-2 bg-zinc-950 flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-white">{(r.unused_sessions||0)} unused · banked at {Math.round((r.rollover_rate||0.85)*100)}%</div>
                                    <div className="text-[11px] text-zinc-500">{new Date(r.created_at).toLocaleDateString()} · expires {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-white text-base font-semibold">${(r.amount_cents/100).toFixed(2)}</div>
                                    <div className={`text-[10px] uppercase tracking-wider ${r.status === "available" ? "text-emerald-400" : "text-zinc-500"}`}>{r.status}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>

            {repriceQuote && (
                <div data-testid="reprice-modal" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRepriceQuote(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-lg">
                        <div className="px-5 py-3 border-b border-zinc-800">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Early-cancel reprice</div>
                            <h4 className="font-display text-xl tracking-wider mt-0.5">{repriceQuote.commitment.display_name}</h4>
                        </div>
                        <div className="p-5 space-y-3 text-sm">
                            <p className="text-zinc-300 text-xs">They used {repriceQuote.quote.sessions_used} sessions at the member rate. At the non-member rate they would have paid more — this charge bills the difference.</p>
                            <table className="w-full text-sm">
                                <tbody>
                                    <tr><td className="text-zinc-400 py-1">Sessions used</td><td className="text-right text-white">{repriceQuote.quote.sessions_used}</td></tr>
                                    <tr><td className="text-zinc-400 py-1">Member rate</td><td className="text-right text-white">${(repriceQuote.quote.member_per_session_cents/100).toFixed(2)}/sess</td></tr>
                                    <tr><td className="text-zinc-400 py-1">Non-member rate</td><td className="text-right text-white">${(repriceQuote.quote.non_member_per_session_cents/100).toFixed(2)}/sess</td></tr>
                                    <tr><td className="text-zinc-400 py-1">Delta</td><td className="text-right text-white">${(repriceQuote.quote.delta_per_session_cents/100).toFixed(2)}/sess</td></tr>
                                    <tr className="border-t border-zinc-800"><td className="text-zinc-400 py-2">Subtotal</td><td className="text-right text-white">${(repriceQuote.quote.subtotal_cents/100).toFixed(2)}</td></tr>
                                    <tr><td className="text-zinc-400 py-1">Tax (10.3%)</td><td className="text-right text-amber-300">${(repriceQuote.quote.tax_cents/100).toFixed(2)}</td></tr>
                                    <tr><td className="text-white py-1 font-semibold">Total</td><td className="text-right text-white font-bold">${(repriceQuote.quote.total_cents/100).toFixed(2)}</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
                            <button type="button" onClick={() => setRepriceQuote(null)} className="btn-ghost-tactical text-xs">Skip</button>
                            <button type="button" data-testid="reprice-charge-btn" onClick={chargeReprice} className="btn-tactical text-xs">Charge now</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function CommsPanel({ announcements }) {
    return (
        <SectionCard title={`Announcements received (${announcements.length})`}>
            {announcements.length === 0 ? (
                <p className="text-sm text-zinc-300">No announcements sent to this account yet.</p>
            ) : (
                <ul className="divide-y divide-zinc-800">
                    {announcements.map((a, i) => (
                        <li key={i} className="py-2.5 text-sm flex justify-between gap-3">
                            <span className="truncate">{a.type === "athlete" ? `→ ${a.name}` : "→ parent"}</span>
                            <span className={`text-xs ${a.status === "sent" ? "text-brand-navy-300" : "text-brand-red"}`}>
                                {a.status} · {new Date(a.sent_at || a.attempted_at).toLocaleString()}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </SectionCard>
    );
}

function StrikesPanel({ userId, parentName }) {
    const [summary, setSummary] = useState(null);
    const [list, setList] = useState(null);
    const [busy, setBusy] = useState(true);
    const [acting, setActing] = useState(null);

    const load = async () => {
        if (!userId) { setBusy(false); return; }
        setBusy(true);
        try {
            const [s, l] = await Promise.all([
                axios.get(`${API}/coach/strikes?user_id=${userId}`, { withCredentials: true }),
                axios.get(`${API}/coach/strikes/list?user_id=${userId}`, { withCredentials: true }),
            ]);
            setSummary(s.data);
            setList(l.data);
        } catch (e) {
            toast.error("Couldn't load strikes", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

    const forgive = async (bookingId) => {
        setActing(bookingId);
        try {
            await axios.post(`${API}/coach/strikes/forgive`, { booking_id: bookingId }, { withCredentials: true });
            toast.success("Strike forgiven");
            await load();
        } catch (e) {
            toast.error("Couldn't forgive", { description: e?.response?.data?.detail });
        } finally { setActing(null); }
    };

    const recredit = async () => {
        setActing("recredit");
        try {
            await axios.post(`${API}/coach/credits/recredit`, { user_id: userId }, { withCredentials: true });
            toast.success("Session re-credited");
            await load();
        } catch (e) {
            toast.error("Couldn't re-credit", { description: e?.response?.data?.detail });
        } finally { setActing(null); }
    };

    if (!userId) {
        return <SectionCard title="Strikes"><p className="text-sm text-zinc-300">No parent account on file.</p></SectionCard>;
    }
    if (busy) return <SectionCard title="Strikes"><div className="flex items-center gap-2 text-zinc-300 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></SectionCard>;
    const cycle = summary?.cycle || "—";
    const late = list?.late_cancels || [];
    const noShow = list?.no_shows || [];
    const formatRow = (r) => {
        const dt = r.date ? new Date(`${r.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "—";
        return `${dt}${r.start_time ? " · " + r.start_time : ""}${r.label ? " · " + r.label : ""}`;
    };

    return (
        <>
            <SectionCard
                title={`Cycle ${cycle} · Strikes`}
                actions={
                    <button type="button" data-testid="strikes-refresh" onClick={load} className="btn-ghost-tactical text-xs inline-flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                }
            >
                <div className="grid grid-cols-2 gap-4">
                    <div data-testid="strike-counter-late" className="border border-zinc-800 rounded-lg p-4">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Late cancels</div>
                        <div className="font-display text-3xl tracking-wider mt-1">{summary?.late_cancels ?? 0}</div>
                        <p className="text-[11px] text-zinc-400 mt-1">2 per cycle deducts 1 paid session.</p>
                    </div>
                    <div data-testid="strike-counter-noshow" className="border border-zinc-800 rounded-lg p-4">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">No-shows</div>
                        <div className="font-display text-3xl tracking-wider mt-1">{summary?.no_shows ?? 0}</div>
                        <p className="text-[11px] text-zinc-400 mt-1">1 grace per cycle, then every no-show deducts.</p>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        data-testid="strike-recredit"
                        onClick={recredit}
                        disabled={acting === "recredit"}
                        className="btn-ghost-tactical text-xs inline-flex items-center gap-1.5"
                    >
                        {acting === "recredit" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                        Re-credit a session
                    </button>
                </div>
                <p className="text-[11px] text-zinc-400 mt-3">
                    {parentName ? `Account: ${parentName}` : ""}. "Forgive" stops a row from counting toward the cycle total. Use "Re-credit a session" if a credit was already burned.
                </p>
            </SectionCard>

            <SectionCard title={`Late cancels (${late.length})`}>
                {late.length === 0 ? (
                    <p className="text-sm text-zinc-300">None this cycle.</p>
                ) : (
                    <ul className="divide-y divide-zinc-800">
                        {late.map((r) => (
                            <li key={r.booking_id} data-testid={`strike-late-${r.booking_id}`} className="py-3 flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm">{formatRow(r)}</div>
                                    <div className="text-[10px] text-zinc-400 mt-0.5">
                                        Cancelled {r.cancelled_at ? new Date(r.cancelled_at).toLocaleString() : "—"}
                                        {r.forgiven && <span className="ml-2 text-brand-navy-300">· Forgiven</span>}
                                    </div>
                                </div>
                                {!r.forgiven && (
                                    <button
                                        type="button"
                                        data-testid={`strike-forgive-${r.booking_id}`}
                                        onClick={() => forgive(r.booking_id)}
                                        disabled={acting === r.booking_id}
                                        className="btn-ghost-tactical text-xs"
                                    >
                                        {acting === r.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Forgive"}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </SectionCard>

            <SectionCard title={`No-shows (${noShow.length})`}>
                {noShow.length === 0 ? (
                    <p className="text-sm text-zinc-300">None this cycle.</p>
                ) : (
                    <ul className="divide-y divide-zinc-800">
                        {noShow.map((r) => (
                            <li key={r.booking_id} data-testid={`strike-noshow-${r.booking_id}`} className="py-3 flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm">{formatRow(r)}</div>
                                    <div className="text-[10px] text-zinc-400 mt-0.5">
                                        Marked {r.marked_at ? new Date(r.marked_at).toLocaleString() : "—"}
                                        {r.forgiven && <span className="ml-2 text-brand-navy-300">· Forgiven</span>}
                                    </div>
                                </div>
                                {!r.forgiven && (
                                    <button
                                        type="button"
                                        data-testid={`strike-forgive-${r.booking_id}`}
                                        onClick={() => forgive(r.booking_id)}
                                        disabled={acting === r.booking_id}
                                        className="btn-ghost-tactical text-xs"
                                    >
                                        {acting === r.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Forgive"}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </SectionCard>
        </>
    );
}

function NotesPanel({ value, onChange, onSave, saving, updatedAt }) {
    return (
        <SectionCard title="Private coach notes" actions={
            <button
                type="button"
                data-testid="dossier-save-notes"
                onClick={onSave}
                disabled={saving}
                className="btn-tactical text-xs"
            >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
            </button>
        }>
            <textarea
                data-testid="dossier-notes-input"
                rows={14}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Goals, behaviors, PR history, anything off-record. Athletes never see this."
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
            />
            {updatedAt && (
                <p className="text-[11px] text-zinc-300 mt-2">Last edited {new Date(updatedAt).toLocaleString()}</p>
            )}
        </SectionCard>
    );
}


/**
 * TierCheckboxes — two checkboxes on the dossier header:
 *   [☑/☐] Member         — auto from active subscription; can be manually pinned
 *   [☑/☐] Member+        — manual-only, owner override
 *
 * Clicking either checkbox sets a *manual* tier on the user. To return to
 * automatic (subscription-driven), the owner uses the small "Auto" link
 * which clears the manual override.
 */
function TierCheckboxes({ userId, tier, manual, onSaved }) {
    const [busy, setBusy] = useState(false);
    const isMemberPlus = tier === "member_plus";
    const isMember = tier === "member" || isMemberPlus;

    const setManual = async (nextTier) => {
        setBusy(true);
        try {
            await axios.patch(
                `${API}/coach/clients/${userId}/tier`,
                { tier: nextTier, manual: true },
                { withCredentials: true },
            );
            onSaved?.(nextTier, true);
            toast.success(`Tier set to ${nextTier.replace("_", " ")}`);
        } catch (e) {
            toast.error("Couldn't update tier", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const clearManual = async () => {
        setBusy(true);
        try {
            const r = await axios.patch(
                `${API}/coach/clients/${userId}/tier`,
                { manual: false },
                { withCredentials: true },
            );
            const newTier = r.data?.tier || "non_member";
            onSaved?.(newTier, false);
            toast.success(`Auto: ${newTier.replace("_", " ")}`);
        } catch (e) {
            toast.error("Couldn't clear override", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const toggleMember = () => {
        // Clicking Member while it's checked + manual → drop back to non_member (manual)
        if (isMember && manual && !isMemberPlus) { setManual("non_member"); return; }
        // Clicking Member while it's checked via auto-sub → pin to non_member (override)
        if (isMember && !manual) { setManual("non_member"); return; }
        // Clicking unchecked Member → promote to member (manual)
        setManual("member");
    };
    const toggleMemberPlus = () => {
        if (isMemberPlus) { setManual("member"); return; }  // step down
        setManual("member_plus");
    };

    return (
        <div className="inline-flex items-center gap-3 px-2 py-1 border border-zinc-800 bg-zinc-900/50">
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">Tier</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] tracking-[0.2em] uppercase text-white">
                <input
                    type="checkbox"
                    data-testid="dossier-tier-member"
                    checked={isMember}
                    disabled={busy}
                    onChange={toggleMember}
                    className="accent-brand-red"
                />
                Member
                {!manual && isMember && (
                    <span className="text-[9px] tracking-[0.2em] text-emerald-400 normal-case ml-1">(auto)</span>
                )}
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] tracking-[0.2em] uppercase text-white">
                <input
                    type="checkbox"
                    data-testid="dossier-tier-member-plus"
                    checked={isMemberPlus}
                    disabled={busy}
                    onChange={toggleMemberPlus}
                    className="accent-brand-red"
                />
                Member+
            </label>
            {manual && (
                <button
                    type="button"
                    data-testid="dossier-tier-clear"
                    onClick={clearManual}
                    disabled={busy}
                    className="text-[9px] tracking-[0.2em] uppercase text-zinc-500 hover:text-emerald-400 underline-offset-2 hover:underline"
                    title="Drop manual override — tier follows subscription"
                >
                    Use auto
                </button>
            )}
        </div>
    );
}

/** Legacy dropdown — kept temporarily; not used. */
// eslint-disable-next-line no-unused-vars
function TierDropdown({ userId, value, onSaved }) {
    const [val, setVal] = useState(value || "non_member");
    const [busy, setBusy] = useState(false);
    const onChange = async (next) => {
        setVal(next);
        setBusy(true);
        try {
            await axios.patch(
                `${API}/coach/clients/${userId}/tier`,
                { tier: next },
                { withCredentials: true },
            );
            onSaved?.(next);
            toast.success(`Tier updated to ${next.replace("_", "+")}`);
        } catch (e) {
            toast.error("Couldn't update tier", { description: e?.response?.data?.detail });
            setVal(value || "non_member");
        } finally {
            setBusy(false);
        }
    };
    return (
        <label className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase">
            <span className="text-zinc-400">Tier</span>
            <select
                data-testid="dossier-tier-select"
                value={val}
                onChange={(e) => onChange(e.target.value)}
                disabled={busy}
                className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-white focus:outline-none focus:border-brand-red"
            >
                <option value="non_member">Non-member</option>
                <option value="member">Member</option>
                <option value="member_plus">Member+</option>
            </select>
        </label>
    );
}


/**
 * (Moved to /components/coach/PackPricingMatrix.jsx as PackPricingMatrixWithStatus.)
 */

